import type { z } from "zod";

import type { RouteContract } from "./contracts/types";

/**
 * Typed client for first-party /api routes, driven by a RouteContract.
 *
 * Design (settled — see docs/API_CONTRACTS.md):
 *   - Returns a `{ ok }` discriminated union; NEVER throws (network errors and
 *     non-2xx both become `{ ok: false }`). Mirrors `ActionResult` /
 *     `requireApiAuth` in the rest of the codebase.
 *   - CASTS the response to the contract's inferred type; does NOT `.parse()` it
 *     at runtime (zod stays out of the response hot path). Drift is caught in
 *     contract tests, not in production.
 *
 * `apiSend` is the primitive.
 */
export type ApiError = { status: number; message: string; body: unknown };

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiError };

export type ApiSendOptions<TReq extends z.ZodTypeAny | null> = {
  /** Path params for parameterized contracts (e.g. { id } for /api/chats/:id/...). */
  params?: Record<string, string>;
  /** Forwarded to fetch — e.g. keepalive for fire-and-forget on unload. */
  keepalive?: boolean;
  signal?: AbortSignal;
  cache?: RequestCache;
} & (TReq extends z.ZodTypeAny ? { body: z.input<TReq> } : { body?: never });

export async function apiSend<
  TReq extends z.ZodTypeAny | null,
  TRes extends z.ZodTypeAny,
>(
  contract: RouteContract<TReq, TRes>,
  options: ApiSendOptions<TReq> = {} as ApiSendOptions<TReq>,
): Promise<ApiResult<z.infer<TRes>>> {
  const path =
    typeof contract.path === "function"
      ? contract.path(options.params ?? {})
      : contract.path;

  const sendBody = contract.request !== null && options.body !== undefined;

  let res: Response;
  try {
    res = await fetch(path, {
      method: contract.method,
      headers: sendBody ? { "Content-Type": "application/json" } : undefined,
      body: sendBody ? JSON.stringify(options.body) : undefined,
      keepalive: options.keepalive,
      signal: options.signal,
      cache: options.cache,
    });
  } catch (e) {
    // Network failure / abort — surface as a uniform error, never throw.
    return {
      ok: false,
      error: {
        status: 0,
        message: e instanceof Error ? e.message : "Network error.",
        body: null,
      },
    };
  }

  const json = (await res.json().catch(() => null)) as unknown;

  if (!res.ok) {
    return {
      ok: false,
      error: {
        status: res.status,
        message: errorMessage(json, res.status),
        body: json,
      },
    };
  }

  // Cast, do not parse — see module doc.
  return { ok: true, data: json as z.infer<TRes> };
}

/**
 * Every route returns `{ message }` on failure (the codebase-wide error
 * envelope). Pull it out, falling back to a status-coded default.
 */
function errorMessage(json: unknown, status: number): string {
  if (
    json &&
    typeof json === "object" &&
    "message" in json &&
    typeof (json as { message: unknown }).message === "string"
  ) {
    return (json as { message: string }).message;
  }
  return `Request failed (${status}).`;
}
